import React from 'react';
import styles from './ProjectView.module.css';
import { ProjectCard } from '@shared/components/ProjectCard/ProjectCard';
import { ProjectList } from '@shared/components/ProjectList/ProjectList';
import { BaseProjectProps } from '@shared/components/ProjectCard/utils';

/**
 * Props for the ProjectView component
 */
export interface ProjectViewProps {
  /** Current user object for permission-based menu action filtering */
  user?: { id?: string; email?: string };
  /** Array of projects to display (can be card or list format) */
  projects: BaseProjectProps[];
  /** Number of columns for card view (0 = responsive auto-fill, 1-4 = fixed columns) */
  columns?: 0 | 1 | 2 | 3 | 4;
  /** Gap between items in pixels */
  gap?: number;
  /** Display mode: 'grid-view' for grid layout, 'list-view' for vertical list */
  viewType?: 'grid-view' | 'list-view';
  /** Callback when a project is clicked */
  onProjectClick?: (projectId: string) => void;
  /** Callback when a menu action is triggered */
  onProjectMenuAction?: (projectId: string, actionId: string) => void;
  /** Text to display when values are not entered (applied to all projects) */
  notEntered?: string;
}

/**
 * ProjectView - A flexible component for displaying projects in either card grid or list layout
 * 
 * @example
 * // Card view with 4 columns
 * <ProjectView projects={projects} viewType="grid-view" columns={4} />
 * 
 * @example
 * // List view
 * <ProjectView projects={projects} viewType="list" />
 */
export const ProjectView: React.FC<ProjectViewProps> = ({
  user,
  projects,
  columns = 0,
  gap = 24,
  viewType = 'grid-view',
  onProjectClick,
  onProjectMenuAction,
  notEntered = ''
}) => {
  
  // List view: vertical stack of horizontal bars
  if (viewType === 'list-view') {
    return (
      <div
        className={styles.listViewContainer}
        style={{ gap: `${gap}px` }}
        data-test="project-list-container"
      >
        {projects.map((project) => (
          <ProjectList
            key={project.id}
            {...(project as BaseProjectProps)}
            user={user}
            onClick={() => onProjectClick?.(project.id)}
            onMenuAction={onProjectMenuAction}
            notEntered={project.notEntered || notEntered}
          />
        ))}
      </div>
    );
  }

  // Card view: responsive grid layout
  // Use fixed columns if specified (1-4), 0 or undefined means responsive auto-fill
  const gridColumns = (columns && columns > 0)
    ? `repeat(${columns}, 1fr)`
    : 'repeat(auto-fill, 295px)';

  return (
    <div
      className={styles.cardViewContainer}
      style={{
        gridTemplateColumns: gridColumns,
        gap: `${gap}px`
      }}
      data-test="project-card-container"
    >
      {projects.map((project) => (
        <ProjectCard
          key={project.id}
          {...(project as BaseProjectProps)}
          user={user}
          onClick={() => onProjectClick?.(project.id)}
          onMenuAction={onProjectMenuAction}
          notEntered={project.notEntered || notEntered}
        />
      ))}
    </div>
  );
};
